import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import { Edit } from '@icons/common';

class EditableSubject extends React.PureComponent {
  static getDerivedStateFromProps({ value }, { initialValue }) {
    if (value === initialValue) return null;

    return {
      initialValue: value,
      value,
      ...(value ? { error: null } : {}),
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      initialValue: props.value,
      isEditing: false,
      value: props.value,
      error: null,
    };

    this.textarea = React.createRef();
    this.handleCancel = this.handleCancel.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.resizeTextarea = this.resizeTextarea.bind(this);
    this.toggleEdit = this.toggleEdit.bind(this);
  }

  handleChange({ target }) {
    this.setState({ value: target.value }, this.resizeTextarea);
  }

  handleSubmit() {
    const { onSubmit } = this.props;
    const { value } = this.state;

    if (!value?.trim()) {
      this.setState({ error: 'Тема не должна быть пустой', value: null });
      onSubmit({ value: null });
      this.toggleEdit(false);
      return;
    }

    onSubmit({ value });
    this.toggleEdit(false);
  }

  handleCancel() {
    const { initialValue } = this.state;
    this.setState({ value: initialValue });
    this.toggleEdit(false);
  }

  handleKeyDown({ key }) {
    if (key === 'Backspace') {
      this.textarea.current.style.height = this.minTextareaHeight;
    }

    if (key === 'Enter') {
      this.handleSubmit();
    }

    if (key === 'Escape') {
      this.handleCancel();
    }
  }

  toggleEdit(isEditing) {
    const { readOnly } = this.props;

    if (readOnly) {
      return;
    }

    if (isEditing) {
      this.setState({ error: null });
    }

    this.setState({ isEditing }, () => this.initialResize());
  }

  initialResize() {
    if (!this.textarea.current) {
      return;
    }

    this.minTextareaHeight = window
      .getComputedStyle(this.textarea.current)
      .getPropertyValue('min-height');

    this.textarea.current.style.height = this.minTextareaHeight;
    this.resizeTextarea();
  }

  resizeTextarea() {
    const el = this.textarea.current;
    const { scrollHeight } = el;
    el.style.height = `${scrollHeight}px`;
  }

  render() {
    const { isEditing, value, error } = this.state;
    const { placeholder, readOnly, isRequired, ...props } = this.props;
    return (
      <StyledEditableSubject {...props}>
        {isEditing ? (
          <StyledTextarea
            autoFocus
            value={value}
            ref={this.textarea}
            placeholder={placeholder}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onBlur={this.handleSubmit}
          />
        ) : (
          <StyledEditButton
            title="Редактировать"
            onClick={() => this.toggleEdit(true)}
          >
            <StyledValue>
              {value}
              {!value && (
                <StyledPlaceholder isRequired={isRequired}>
                  {placeholder}
                </StyledPlaceholder>
              )}
              {!readOnly && <StyledEditIcon />}
            </StyledValue>
          </StyledEditButton>
        )}
        {error && <StyledErrorNotification>{error}</StyledErrorNotification>}
      </StyledEditableSubject>
    );
  }
}

EditableSubject.propTypes = {
  className: PropTypes.string,
  onSubmit: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  value: PropTypes.string,
};

EditableSubject.defaultProps = {
  className: '',
  onSubmit: Function.prototype,
  placeholder: null,
  readOnly: false,
  value: '',
};

const StyledErrorNotification = styled.span`
  display: block;
  font-size: 12px;
  font-weight: normal;
  color: red;
`;

const StyledEditIcon = styled(Edit)`
  margin-left: 5px;
  margin-bottom: -2px;
  opacity: 0;
  cursor: pointer;
  width: 14px;
  height: 15px;

  path {
    stroke: #007aff;
  }
`;

const StyledEditableSubject = styled.div`
  color: #17181a;
  font-size: 18px;
  font-weight: bold;

  :hover {
    ${StyledEditIcon} {
      opacity: 1;
    }
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  resize: none;
  font-size: 18px;
  font-family: 'Backpack';
  background: none;
  min-height: 28px;
  border: none;
  padding: 2px;
  font-weight: bold;
`;

const StyledPlaceholder = styled.span`
  color: #757575;

  ${({ isRequired }) =>
    isRequired &&
    css`
      &::after {
        content: ' *';
        color: red;
      }
    `}
`;

const StyledValue = styled.span`
  display: inline-block;
`;

const StyledEditButton = styled.span`
  margin-left: 2px;
  font-size: 18px;
  min-height: 28px;
  padding: 2px 0;
`;

EditableSubject.StyledTextarea = StyledTextarea;

export default EditableSubject;
